import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import {
  addMailboxMember,
  createMailbox,
  removeMailboxMember,
  testMailboxConnection,
  updateMailbox,
  type MailboxCreateInput,
  type MailboxUpdateInput,
} from "@/lib/mailboxesApi";

import { MAILBOXES_QUERY_KEY } from "./useMailboxes";

function handleError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    const body = error.body as { error?: string } | null;
    if (body?.error) {
      toast.error(body.error);
      return;
    }
  }
  const message = error instanceof Error ? error.message : String(error);
  toast.error(`${fallback}: ${message}`);
}

function useInvalidateMailboxes() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: MAILBOXES_QUERY_KEY });
}

export function useCreateMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (input: MailboxCreateInput) => createMailbox(input),
    onSuccess: () => {
      toast.success("Mailbox creata");
      void invalidate();
    },
    onError: (error) => handleError(error, "Creazione fallita"),
  });
}

export function useUpdateMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MailboxUpdateInput }) =>
      updateMailbox(id, input),
    onSuccess: () => {
      toast.success("Mailbox aggiornata");
      void invalidate();
    },
    onError: (error) => handleError(error, "Aggiornamento fallito"),
  });
}

export function useTestMailboxConnection() {
  return useMutation({
    mutationFn: (id: string) => testMailboxConnection(id),
    onError: (error) => handleError(error, "Test connessione fallito"),
  });
}

export function useAddMailboxMember() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: ({ mailboxId, userId }: { mailboxId: string; userId: string }) =>
      addMailboxMember(mailboxId, userId),
    onSuccess: () => {
      toast.success("Membro aggiunto");
      void invalidate();
    },
    onError: (error) => handleError(error, "Aggiunta membro fallita"),
  });
}

export function useRemoveMailboxMember() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: ({ mailboxId, userId }: { mailboxId: string; userId: string }) =>
      removeMailboxMember(mailboxId, userId),
    onSuccess: () => {
      toast.success("Membro rimosso");
      void invalidate();
    },
    onError: (error) => handleError(error, "Rimozione membro fallita"),
  });
}
